/**
* Copyright Aquera Inc 2023
*
* This source code is licensed under the BSD-3-Clause license found in the
* LICENSE file in the root directory of this source tree.
*/

import {css} from 'lit';

/**
 * FileUpload CSS
 */
export const styles = css`
  :host {
    display: inline-block;
    width: 100%;
  }

  * {
    padding: 0px;
    margin: 0px;
    box-sizing: border-box;
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
  }

  input {
    display: none;
  }

  .horizontal-div {
    height: 62px;
    width: 100%;
    min-width: 230px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
    border: 1px dashed var(--nile-colors-neutral-500, var(--ng-colors-border-neutral));
    color: var(--nile-colors-dark-900, var(--ng-colors-text-primary-900));
    background-color: var(--nile-colors-white-base, var(--ng-colors-bg-primary));
  }

  @media (width < 400px) {
    .browse-file-button {
      display: none;
    }

    .horizontal-div {
      cursor: pointer;
    }
  }

  .hover-border {
    border-color: var(--nile-colors-primary-600, var(--ng-colors-border-brand-alt));
  }

  .title {
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    color: var(--nile-colors-dark-900, var(--ng-colors-text-primary-900));
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 454px;
    width: 100%;
  }

  .subtitle {
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    color: var(--nile-colors-dark-500, var(--ng-colors-text-tertiary-600));
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 454px;
  }

  .vertical-div {
    min-height: 156px;
    height: auto;
    min-width: 240px;
    width: 240px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
    color: var(--nile-colors-dark-900, var(--ng-colors-text-primary-900));
    border: 1px dashed var(--nile-colors-neutral-500, var(--ng-colors-border-neutral));
    background-color: var(--nile-colors-white-base, var(--ng-colors-bg-primary));
    position: relative;
  }

  .vertical-div:hover {
    border-color: var(--nile-colors-blue-500, var(--ng-colors-border-brand));
  }

  .outer-div-border {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .inner-div {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 18px;
    box-sizing: border-box;
  }

  .content {
    display: flex;  
    flex-direction: column;
    justify-content: center;
    gap: 8px;
  }

  .content h4 {
    margin: 0px;
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    color: var(--nile-colors-dark-900, var(--ng-colors-text-primary-900));
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 454px;
  }
      
  .content p {
    margin: 0px;
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    color: var(--nile-colors-neutral-700, var(--ng-colors-text-secondary-700));
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 454px;
  }

  ::slotted(img), ::slotted(nile-icon), ::slotted(video) {
    --nile-svg-height: 24px !important ;
    --nile-svg-width: 24px !important ;
    height:42px;
    width: 42px;
    object-fit: cover;
  }

  .browse-file-button {
    cursor: pointer;
  }

  nile-button::part(base) {
    border-color: var(--nile-colors-neutral-500, var(--ng-colors-border-neutral));
  }

  nile-button:active::part(base) {
    border-color: var(--nile-colors-neutral-700, var(--ng-colors-border-neutral));
  }

  nile-button[disabled]::part(base) {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  /*  DISABLED STATE */
  .disable {
    background-color: var(--nile-colors-dark-200, var(--ng-colors-bg-disabled));
    opacity: 0.8;
    cursor: not-allowed;
    color: var(--nile-colors-neutral-500, var(--ng-colors-text-disabled));
    pointer-events: none;
  }

  /* DRAGGING */
  .dragging {
    background-color: var(--nile-colors-blue-100, var(--ng-colors-bg-brand-secondary));
    border-color: var(--nile-colors-primary-600, var(--ng-colors-border-brand-alt));
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 18px;
    color: var(--nile-colors-blue-700, var(--ng-colors-text-brand-secondary-700));
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .icons-container {
    display: flex;
    gap: 4px;
  }

  .general-icon {
    transform: rotate(-18.11deg);
    position: relative;
    bottom: 3px;
  }

  .image-icon {
    transform: rotate(15deg);
  }

  .icon:hover {
    cursor: pointer;
  }

  /* Uploading State */
  .uploading {
    width: 648px;
    min-width: 230px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 18px;
    box-sizing: border-box;
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary))
  }

  .uploading:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .progress-bar-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0px;
  }

  .progress-bar-percent {
    width: 100%;
    display: flex;
    justify-content: space-between;
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .progress-bar-container p {
    margin: 0px;
  }

  .progress-bar-container > p {
    font-size: var(--nile-type-scale-1, var(--ng-font-size-text-xs));
    color: var(--nile-colors-dark-500, var(--ng-colors-text-tertiary-600));
  }

  /* PREVIEW STATE */
  .preview {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .preview:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .preview-inner {
    display: flex;
    gap: 18px;
  }

  .preview-image-container {
    height: 42px;
    width: 42px;
  }

  .preview-image-container img {
    height: 42px;
    width: 42px;
    object-fit: contain;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }
  
  .preview-file-info {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 8px;
  }

  .preview-file-info p {
    margin: 0px;
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .preview-file-info p:last-of-type{
    color: var(--nile-colors-neutral-700, var(--ng-colors-text-secondary-700));
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .preview nile-icon:hover {
    cursor: pointer;
  }

  /* NO PREVIEW STATE */
  .no-preview {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .no-preview:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .no-preview-container {
    display: flex;
    gap: 18px;
  }

  .document-icon {
    height: 40px;
    width: 40px;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
    padding: 2px 3px 2px 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--nile-colors-blue-400, var(--ng-componentcolors-utility-blue-400));
    background-color: var(--nile-colors-blue-100, var(--ng-colors-bg-brand-secondary));
  }

  .document-icon nile-icon {
    height: 40px;
    width: 40px;
  }
  
  .preview-file-info {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 8px;
  }

  .preview-file-info p {
    margin: 0px;
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .preview-file-info p:last-of-type{
    color: var(--nile-colors-neutral-700, var(--ng-colors-text-secondary-700));
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .no-preview > nile-icon:hover {
    cursor: pointer;
  }

  /* ERROR STATE */
  
  .error {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .error:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .error-container {
    width: 100%;
    display: flex;
    gap: 18px;
  }

  .error-icon {
    height: 40px;
    width: 40px;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
    padding: 2px 3px 2px 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--nile-colors-red-400, var(--ng-colors-border-error-subtle));
    background-color: var(--nile-colors-red-100, var(--ng-colors-bg-error-secondary));
  }

  .error-icon nile-icon {
    height: 40px;
    width: 40px;
  }
  
  .file-info {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 8px;
  }

  .file-info p {
    margin: 0px;
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .file-info p:last-of-type{
    color: var(--nile-colors-red-700, var(--ng-colors-text-error-primary-600));
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
  }

  .error > nile-icon:hover {
    cursor: pointer;
  }

  /* VARTICAL STATE START */
  /* Default State */

  .vertical-default {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px
  }

  .content-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  .content-container div.title {
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    color: var(--nile-colors-dark-900, var(--ng-colors-text-primary-900));
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 222px;
  }

  .content-container div.subtitle {
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    color: var(--nile-colors-dark-500, var(--ng-colors-text-tertiary-600));
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 222px;
  }

  /* Drag State */
  .vertical-drag {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap:18px;
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    color: var(--nile-colors-blue-700, var(--ng-colors-text-brand-secondary-700));
    background-color: var(--nile-colors-blue-100, var(--ng-colors-bg-brand-secondary));
    border-color: var(--nile-colors-blue-500, var(--ng-colors-border-brand));
  }

  /* Uploading State */
  .vertical-uploading {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .vertical-uploading:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .corner-icon {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  /* Preview State */
  .vertical-preview {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .vertical-preview:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .image-preview {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
    object-fit: cover;
    width: 80px;
    height: 80px;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
  }

  /* Vertical No Preview State */
  .vertical-no-preview {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .vertical-no-preview:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .vertical-document-icon {
    height: 80px;
    width: 80px;
    padding: 2px 3px 2px 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
    border: 1px solid var(--nile-colors-blue-400, var(--ng-componentcolors-utility-blue-400));
    background-color: var(--nile-colors-blue-100, var(--ng-colors-bg-brand-secondary));
  }

  /* Veritcal Error State */
  .vertical-error {
    border: 1px solid var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .vertical-error:hover {
    border-color: var(--nile-colors-neutral-400, var(--ng-colors-border-primary));
  }

  .error-bg {
    background-color: var(--nile-colors-red-100, var(--ng-colors-bg-error-secondary));
    border: 1px solid var(--nile-colors-red-400, var(--ng-colors-border-error-subtle));
  }

  .error-message {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin: 0px;
    font-size: var(--nile-type-scale-3, var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    text-align: start;
  }

  .error-p {
    color: var(--nile-colors-red-700, var(--ng-colors-text-error-primary-600));
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    width: 216px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }

  .error-p p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }


  /* Displaing uploaded files CSS */
  .display-files {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    width: 648px;
    min-width: 230px;
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--nile-colors-neutral-700, var(--ng-colors-border-neutral));
    border-radius: var(--nile-radius-radius-xs, var(--ng-radius-xs));
    scrollbar-width: none;
    padding: 12px;
  }

  ul, li {
    width: 100%;
  }

  li {
    list-style-type: none;
  }

  /* for the upload error messages */
  .upload-error {
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    font-weight: var(--nile-font-weight-regular, var(--ng-font-weight-regular));
    color: var(--nile-colors-red-700, var(--ng-colors-text-error-primary-600));
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: var(--nile-spacing-one-half-x, var(--ng-spacing-xxs));
    margin-top: 10px;
  }

  .upload-error > nile-tooltip > span {
    color: var(--nile-colors-red-700, var(--ng-colors-text-error-primary-600));
    white-space: nowrap;
    text-align: left;
    max-width: 560px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .vertical-upload-error {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 10px;
  }

  .vertical-upload-error nile-tooltip span, .vertical-upload-error span {
    color: var(--nile-colors-red-700, var(--ng-colors-text-error-primary-600));
    white-space: nowrap;
    text-align: left;
    width: 222px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    display: inline-block;
  }

  .upload-error nile-lite-tooltip,
  .vertical-upload-error nile-lite-tooltip {
    display: flex;
    align-items: center;
    font-size: var(--nile-type-scale-2, var(--ng-font-size-text-xs));
    line-height: 1;
  }
`;

export default [styles];